import React, { useEffect, useState, useMemo } from 'react'
import { Column } from 'react-table';
import LineChart from '../../components/chartTypes/lineChart';
import MultiSelectCheckboxGroup from '../../components/CheckBoxFilter';
import { generatePrincipalDPDEwi } from '../../services/delinquencyReports';
import { generateDeliquencyLinechart } from '../../services/summaryReport';
import VerticalTable from '../../components/verticalTable';
import { useTranslation } from 'react-i18next';
import { sixMonthsAgoFormatted, twoDaysAgoFormatted } from '../../constants/intex';
import { baseURL } from '../../services';
import Loader from '../../components/Loader';
import { listBranches } from '../../services/filterApis';
import Table2 from '../../components/Table2';
import VerticalTable3 from '../../components/verticalTable3';

// Updated Branch colors for comparative mode
const BRANCH_COLORS = [
  { 
    bgColor: '#E3F2FD',  // Light Blue background
    textColor: '#0D47A1', // Dark Blue text
    borderColor: '#90CAF9' // Blue border
  },
  { 
    bgColor: '#E8F5E9',  // Light Green background
    textColor: '#1B5E20', // Dark Green text
    borderColor: '#A5D6A7' // Green border
  },
  { 
    bgColor: '#FFF3E0',  // Light Orange background
    textColor: '#E65100', // Dark Orange text
    borderColor: '#FFCC80' // Orange border
  }
];

type BranchColor = {
  bgColor: string;
  textColor: string;
  borderColor: string;
};

const DeliquencyRatesEWI = () => {
  const { t } = useTranslation();

  type TableRow = {
    category: string;
    values: string[];
  };

  interface Order {
    month: string;
    dpd1: string;
    dpd2: string;
    dpd3: string;
    dpd4: string;
    dpd5: string;
    dpd6: string;
    dpd7: string;
    dpd8: string;
    dpd9: string;
  }

  const columns: Column<any>[] = [
    { Header: "Month", accessor: "month" },
    { Header: "00. current-7", accessor: "dpd1" },
    { Header: "01. 8-29 DPD", accessor: "dpd2" },
    { Header: "02. 30-59 DPD", accessor: "dpd3" },
    { Header: "03. 60-89 DPD", accessor: "dpd4" },
    { Header: "04. 90-119 DPD", accessor: "dpd5" },
    { Header: "05. 120-149 DPD", accessor: "dpd6" },
    { Header: "06. 150-179 DPD", accessor: "dpd7" },
    { Header: "11. 180+ DPD", accessor: "dpd8" },
    { Header: "Total", accessor: "dpd9" },
  ];

  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"]
  }));

  const [pendingFilters, setPendingFilters] = useState<Record<string, string[]>>(() => ({
    periodWithOutDay: ["month"],
    fromDate: [sixMonthsAgoFormatted],
    toDate: [twoDaysAgoFormatted],
    displaymode: ["1"]
  }));

  const [applyFilter, setApplyFilter] = useState(0);
  const [comparativeLoading, setComparativeLoading] = useState(false);
  const [comparativeTables, setComparativeTables] = useState<any[][]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(true);
  const [isDownloadDisabled, setIsDownloadDisabled] = useState(false);
  const [branchColors, setBranchColors] = useState<Record<string, BranchColor>>({});
  
  const filters = [
    { key: "state", type: "select", label: t("filters.state") },
    { key: "cluster", type: "select", label: t("filters.cluster") },
    {
      key: "displaymode",
      type: "singleSelect",
      label: t("filters.displaymode"),
    },
    { key: "branch", type: "select", label: t("filters.branch") },
    { key: "periodWithOutDay", type: "singleSelect", label: t("filters.period") },
    { key: "fromDate", type: "date", label: t("filters.fromDate"), maxValue: selectedFilters.toDate },
    { key: "toDate", type: "date", label: t("filters.toDate"), maxValue: twoDaysAgoFormatted },
  ];

  const [branchList, setBranchList] = useState<{ branch_id: string; branch_name: string }[]>([]);
  const [principalDPDEwi, setPrincipalDPDEwi] = useState<{ headers: string[]; data: { category: string; values: string[] }[] }>({
    headers: [],
    data: [],
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [portfolioMovement, setPortfolioMovement] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [portfolioMovementLoading, setPortfolioMovementLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchBranches = async () => {
      try {
        const response = await listBranches(selectedFilters.state || []);
        const branches = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
          ? response
          : [];
        setBranchList(branches);
      } catch (error) {
        console.error("Error loading branches", error);
        setBranchList([]);
      }
    };

    fetchBranches();
  }, [selectedFilters.state]);

  const branchIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (Array.isArray(branchList)) {
      branchList.forEach((branch) => {
        const id = branch?.branch_id;
        const name = branch?.branch_name;
        if (id && name) {
          map[id] = name;
        }
      });
    }
    return map;
  }, [branchList]);

  const getBranchName = (branchId: string): string => {
    if (branchIdToNameMap[branchId]) return branchIdToNameMap[branchId];
    const branch = branchList.find((b) => b.branch_id === branchId);
    if (branch) return branch.branch_name || branchId;
    return branchId;
  };

  const getGeneratePrincipalDPDEwi = async () => {
    const displaymodeRaw = selectedFilters.displaymode?.[0] || "1";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const isComparative = displaymode === "comparative" || displaymode === "2";

    setLoading(true);

    try {
      if (isComparative) {
        setComparativeLoading(true);
        const selectedBranches = selectedFilters.branch || [];

        // Assign colors to branches
        const colors: Record<string, BranchColor> = {};
        selectedBranches.forEach((branchId, index) => {
          colors[branchId] = BRANCH_COLORS[index % BRANCH_COLORS.length];
        });
        setBranchColors(colors);

        const branchPromises = selectedBranches.map((branchId) =>
          generatePrincipalDPDEwi({
            ...selectedFilters,
            branch: [branchId],
          })
        );

        const branchResponses = await Promise.all(branchPromises);
        const comparativeData = branchResponses.map((res) =>
          Array.isArray(res.data) ? res.data : []
        );

        setComparativeTables(comparativeData);

        // Fetch principalDPDEwi for period keys
        const periodResponse = await generatePrincipalDPDEwi({
          ...selectedFilters,
          branch: [], // remove branch to get all-periods view
        });

        const apiData: any[] = periodResponse.data;
        const bucketCategories = apiData[0]?.dpd_buckets.map((bucket: { bucket_name?: string; slab_name?: string }) => ({
          category: bucket.bucket_name || bucket.slab_name || "Unknown",
        })) || [];

        const headers: string[] = ["DPD Bucket", ...apiData.map((item: { period: string }) => item.period)];
        const transformedData = bucketCategories.map(({ category }: { category: string }, index: number) => ({
          category,
          values: apiData.map((item: { dpd_buckets: { percentage?: string }[] }) => item.dpd_buckets[index]?.percentage || "0"),
        }));

        setPrincipalDPDEwi({ headers, data: transformedData });
      } else {
        const response = await generatePrincipalDPDEwi(selectedFilters);
        const apiData: any[] = response.data;

        const bucketCategories = apiData[0]?.dpd_buckets.map((bucket: { bucket_name?: string; slab_name?: string }) => ({
          category: bucket.bucket_name || bucket.slab_name || "Unknown",
        })) || [];

        const headers: string[] = ["DPD Bucket", ...apiData.map((item: { period: string }) => item.period)];
        const transformedData = bucketCategories.map(({ category }: { category: string }, index: number) => ({
          category,
          values: apiData.map((item: { dpd_buckets: { percentage?: string }[] }) => item.dpd_buckets[index]?.percentage || "0"),
        }));

        setPrincipalDPDEwi({ headers, data: transformedData });
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
      setComparativeLoading(false);
    }
  };

  const generatePortfolioNetMovement = async () => {
    setPortfolioMovementLoading(true);
    try {
      const response = await generateDeliquencyLinechart(selectedFilters);
      setPortfolioMovement(response.data);
    } catch (err) {
      setError("Failed to fetch users");
      console.error(err);
    } finally {
      setPortfolioMovementLoading(false);
    }
  };

  const handleApplyClick = () => {
    const displaymodeRaw = pendingFilters.displaymode?.[0] || "consolidate";
    const displaymode = displaymodeRaw.trim().toLowerCase();
    const selectedBranches = pendingFilters.branch || [];
    const isComparative = displaymode === "comparative" || displaymode === "2";

    if (isComparative) {
      if (selectedBranches.length < 2 || selectedBranches.length > 3) {
        setValidationError(
          "Please select minimum two and maximum three branches for comparative mode."
        );
        setIsValid(false);
        setIsDownloadDisabled(true);
        return;
      }
    }

    setSelectedFilters(pendingFilters);
    setValidationError("");
    setIsValid(true);
    setIsDownloadDisabled(false);
    setApplyFilter((prev) => prev + 1);
  };

  const handleFilterChange = (updatedValues: Record<string, string[]>) => {
    setPendingFilters(updatedValues);
  };

  const handleCancelClick = () => {
    setSelectedFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
    setApplyFilter(0);
    setPendingFilters({
      periodWithOutDay: ["month"],
      fromDate: [sixMonthsAgoFormatted],
      toDate: [twoDaysAgoFormatted],
      displaymode: ["1"],
    });
  };

  const handleDownloadClick = () => {
    const link = `${baseURL}generate/portfolio_delinquency/principal_dpd_ewi_csv?from_date=${selectedFilters.fromDate ?? ""}&to_date=${selectedFilters.toDate ?? ""}&group_by=${selectedFilters.periodWithOutDay ?? ""}&state_id=${selectedFilters.state ?? ""}&territory_id=${selectedFilters.territory ?? ""}&cluster_id=${selectedFilters.cluster ?? ""}&branch_id=${selectedFilters.branch ?? ""}`;
    window.location.href = link;
  };

  useEffect(() => {
    getGeneratePrincipalDPDEwi();
    generatePortfolioNetMovement();
  }, [applyFilter]);

  return (
    <div>
      {/* Page Content */}
      <div className="page-content">
        {/* Dynamic Grey Header */}
        <div className="header">
          {t('deliquencyRatesEWI.pageTitle')}
        </div>

        {/* Main Content */}
        <div className="content px-3">
          <MultiSelectCheckboxGroup
            filters={filters}
            selectedValues={pendingFilters}
            onChange={handleFilterChange}
            isLoading={[loading, portfolioMovementLoading, comparativeLoading]}
            buttons={[
              { label: t("buttons.apply"), onClick: () => handleApplyClick() },
              { label: t("buttons.cancel"), onClick: () => handleCancelClick() },
              {
                label: t("buttons.download"),
                onClick: () => handleDownloadClick(),
                disabled: isDownloadDisabled
              },
            ]}
          />
          {/* Show validation error if exists */}
          {validationError && (
            <div
              style={{
                color: "#B00020",
                backgroundColor: "#FDECEA",
                padding: "12px 16px",
                border: "1px solid #F5C6CB",
                borderRadius: "6px",
                fontWeight: "500",
                marginTop: "10px",
                boxShadow: "0 2px 6px rgba(0,0,0,0.1)",
              }}
            >
              ⚠️ {validationError}
            </div>
          )}
        </div>

        {/* PE O/S by DPD EWI*/}
        <div className="p-3">
          {!validationError && (
            <>
              {applyFilter &&
              selectedFilters.displaymode?.[0]?.trim().toLowerCase() === "2" ? (
                <div style={{ width: "100%", overflow: "auto" }}>
                  {(() => {
                    // Build data
                    const branchDataMap = new Map<string, any[]>();
                    (selectedFilters.branch || []).forEach((id, idx) =>
                      branchDataMap.set(id, comparativeTables?.[idx] || [])
                    );

                    const periods =
                      comparativeTables[0]?.map((item: any) => item.period) || [];

                    const dpdBuckets =
                      comparativeTables[0]?.[0]?.dpd_buckets?.map(
                        (b: any) => b.bucket_name || b.slab_name || "Unknown"
                      ) || [];

                    // Create headers
                    const headers: string[] = [
                      "DPD Bucket",
                      ...periods.flatMap((period) =>
                        (selectedFilters.branch || []).map(
                          (brId) => `${period}\n(${getBranchName(brId)})`
                        )
                      ),
                      
                    ];

                    // Create row data
                    const data = dpdBuckets.map((bucketName: string, bi: number) => {
                      const values: string[] = [];

                      periods.forEach((period) => {
                        (selectedFilters.branch || []).forEach((brId) => {
                          const periodObj =
                            branchDataMap
                              .get(brId)
                              ?.find((d: any) => d.period === period) || {};
                          const value =
                            periodObj?.dpd_buckets?.[bi]?.percentage ?? "-";
                          values.push(value);
                        });
                      });

                      return {
                        category: bucketName,
                        values,
                      };
                    });

                    return (
                      <>
                        <h2
                          style={{
                            fontSize: 15,
                            fontWeight: 600,
                            fontFamily: "Arial",
                            textAlign: "start",
                            paddingLeft: 15,
                            color: "#14213d",
                          }}
                        >
                          {t("deliquencyRatesEWI.PE O/S by DPD EWI")}
                        </h2>

                        <VerticalTable3
                          headers={headers}
                          data={data}
                          loading={comparativeLoading}
                          isComparative={true}
                          branchCount={selectedFilters.branch?.length || 1}
                          branchColors={branchColors}
                          branchIds={selectedFilters.branch || []}
                        />
                      </>
                    );
                  })()}
                </div>
              ) : (
                <div style={{ width: "100%", overflow: "auto" }}>
                  <h2
                    style={{
                      fontSize: 15,
                      fontWeight: 600,
                      fontFamily: "Arial",
                      textAlign: "start",
                      paddingLeft: 15,
                      color: "#14213d",
                    }}
                  >
                    {t("deliquencyRatesEWI.PE O/S by DPD EWI")}
                  </h2>
                  <VerticalTable
                    headers={principalDPDEwi.headers}
                    data={principalDPDEwi.data}
                    loading={loading}
                  />
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-1 p-2">
           {!validationError &&
          <LineChart
            title={t('deliquencyRatesEWI.title')}
            labels={portfolioMovement.map(item => item.period)}
            dataSets={[
              { label: "%30+ DPD", data: portfolioMovement.map(item => item.dpd_buckets.find((bucket: any) => bucket.bucket_name === "%30+ DPD")?.percentage || 0), borderColor: "#70e000" },
              { label: "%60+ DPD", data: portfolioMovement.map(item => item.dpd_buckets.find((bucket: any) => bucket.bucket_name === "%60+ DPD")?.percentage || 0), borderColor: "#003f88" },
              { label: "%90+ DPD", data: portfolioMovement.map(item => item.dpd_buckets.find((bucket: any) => bucket.bucket_name === "%90")?.percentage || 0), borderColor: "#FF5733" }
            ]}
            xLabel={t('deliquencyRatesEWI.xLabel')}
            yLabel={t('deliquencyRatesEWI.yLabel')}
          />
          }
        </div>
      </div>
    </div>
  )
}

export default DeliquencyRatesEWI;